/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as HighlightActionCreators from '@app/actions/HighlightActionCreators';
import {type AutocompleteOption, isChannel} from '@app/components/channel/Autocomplete';
import styles from '@app/components/channel/AutocompleteChannel.module.css';
import {AutocompleteItem} from '@app/components/channel/AutocompleteItem';
import * as ChannelUtils from '@app/utils/ChannelUtils';
import {observer} from 'mobx-react-lite';
import type React from 'react';

export const AutocompleteChannel = observer(
	({
		onSelect,
		keyboardFocusIndex,
		hoverIndex,
		options,
		onMouseEnter,
		onMouseLeave,
		rowRefs,
	}: {
		onSelect: (option: AutocompleteOption) => void;
		keyboardFocusIndex: number;
		hoverIndex: number;
		options: Array<AutocompleteOption>;
		onMouseEnter: (index: number) => void;
		onMouseLeave: () => void;
		rowRefs?: React.MutableRefObject<Array<HTMLButtonElement | null>>;
	}) => {
		const channels = options.filter(isChannel);
		return channels.map((option, index) => (
			<AutocompleteItem
				key={option.channel.id}
				icon={ChannelUtils.getIcon(option.channel, {className: styles.channelIcon})}
				name={option.channel.name}
				isKeyboardSelected={index === keyboardFocusIndex}
				isHovered={index === hoverIndex}
				onSelect={() => onSelect(option)}
				onMouseEnter={() => {
					HighlightActionCreators.highlightChannel(option.channel.id);
					onMouseEnter(index);
				}}
				onMouseLeave={onMouseLeave}
				innerRef={
					rowRefs
						? (node) => {
								rowRefs.current[index] = node;
							}
						: undefined
				}
			/>
		));
	},
);
